import { Compass } from "lucide-react";
import { Button, Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@bookdental/ui";
import { useProductTourContext } from "@/components/product-tour/ProductTourProvider";
import { useIsMobile } from "@/lib/use-media-query";
import { cn } from "@/lib/cn";
import type { AjudaDaPagina } from "@/lib/ajuda/tipos";

/** "Como funciona esta página": resumo, passos e dicas; se a página tem tour, o atalho pra ele. */
export function PainelDeAjuda({ ajuda, aberto, onAbertoChange }: { ajuda: AjudaDaPagina; aberto: boolean; onAbertoChange: (v: boolean) => void }) {
  const isMobile = useIsMobile();
  const { tourDaPagina, startPageTour } = useProductTourContext();

  const fazerTour = () => {
    if (!tourDaPagina) return;
    onAbertoChange(false);
    // O overlay do Sheet (Radix) e o do Joyride brigam: o tour só começa depois que o painel fechou.
    requestAnimationFrame(() => {
      tourDaPagina.onBeforeStart?.();
      startPageTour(tourDaPagina.steps);
    });
  };

  return (
    <Sheet open={aberto} onOpenChange={onAbertoChange}>
      <SheetContent
        side={isMobile ? "bottom" : "right"}
        className={cn(
          "flex flex-col gap-0 overflow-y-auto border-zinc-200 bg-white p-0 shadow-2xl dark:border-border dark:bg-card sm:max-w-md",
          isMobile ? "max-h-[85vh]" : "h-full",
        )}
      >
        <SheetHeader className="border-b border-zinc-100 px-5 py-4 text-left dark:border-border">
          <SheetTitle className="pr-6 text-base">Como funciona: {ajuda.titulo}</SheetTitle>
          <SheetDescription className="text-[13px] leading-relaxed text-zinc-600 dark:text-muted-foreground">{ajuda.resumo}</SheetDescription>
        </SheetHeader>
        <div className="space-y-5 px-5 py-4">
          <ol className="space-y-3">
            {ajuda.passos.map((passo, i) => (
              <li key={i} className="flex gap-3 text-[13.5px] leading-relaxed text-zinc-800 dark:text-foreground">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-50 text-[12px] font-semibold text-brand-700 dark:bg-primary/15 dark:text-primary">
                  {i + 1}
                </span>
                <span className="pt-0.5">{passo}</span>
              </li>
            ))}
          </ol>
          {ajuda.dicas?.length ? (
            <div className="rounded-lg border border-zinc-200 bg-zinc-50 px-4 py-3 dark:border-border dark:bg-muted/30">
              <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Dicas</p>
              <ul className="list-disc space-y-1 pl-4 text-[13px] leading-relaxed text-zinc-700 dark:text-foreground">
                {ajuda.dicas.map((d, i) => (
                  <li key={i}>{d}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {tourDaPagina && (
            <Button type="button" variant="outline" className="w-full gap-2" onClick={fazerTour}>
              <Compass className="h-4 w-4" />
              Fazer o tour desta página
            </Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
